'use client';

import {
    Dispatch,
    HTMLAttributes,
    Ref,
    SetStateAction,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import timeSliderStyles from './TimeSlider.module.css';

export type CountdownApi = {
    start(): void;
    pause(): void;
    stop(): void;
    restart(): void;

    setTime: Dispatch<SetStateAction<number>>;

    isRunning(): boolean;
    isPaused(): boolean;
    isStopped(): boolean;
    isCompleted(): boolean;
};

const zeroPad = (val: number) => val.toString().padStart(2, '0');

function Countdown(
    props: {
        initialTime?: number; // in seconds
        targetTimestamp?: number; // in milliseconds
        autoStart?: boolean;

        onStart?(): void;
        onPause?(): void;
        onStop?(): void;
        onTick?(time: number): void;
        onComplete?(): void;
    } & Pick<HTMLAttributes<HTMLDivElement>, 'className'>,
    ref: Ref<CountdownApi>
) {
    const { className, targetTimestamp, autoStart = true, onStart, onPause, onStop, onTick, onComplete } = props;

    const calculateInitialTime = useCallback(() => {
        if (!targetTimestamp || targetTimestamp <= 0) return 0;
        const calculatedTime = Math.floor((targetTimestamp - new Date().getTime()) / 1000);
        return Math.max(0, calculatedTime);
    }, [targetTimestamp]);

    const [time, setTime] = useState(calculateInitialTime());
    const initialTime = useRef(calculateInitialTime());
    const timerId = useRef(0);

    const isRunning = useCallback(() => timerId.current !== 0, []);

    const isPaused = useCallback(() => !isRunning() && time !== initialTime.current, [isRunning, time]);

    const isStopped = useCallback(() => !isRunning() && time === initialTime.current, [isRunning, time]);

    const isCompleted = useCallback(() => time <= 0, [time]);

    const clearInterval = () => {
        window.clearInterval(timerId.current);
        timerId.current = 0;
    };

    const start = useCallback(() => {
        if (isRunning()) return;

        if (isCompleted()) {
            setTime(initialTime.current);
        }

        timerId.current = window.setInterval(() => {
            setTime((currentTime) => {
                if (!targetTimestamp || targetTimestamp <= 0) {
                    clearInterval();
                    return 0;
                }

                const timeToTarget = Math.floor((targetTimestamp - new Date().getTime()) / 1000);
                const newTime = Math.max(0, targetTimestamp ? timeToTarget : currentTime - 1);

                if (onTick) onTick(newTime);

                if (newTime <= 0) {
                    clearInterval();
                    if (onComplete) onComplete();
                }

                return newTime;
            });
        }, 1000);

        if (onStart) onStart();
    }, [isCompleted, isRunning, onComplete, onStart, onTick, targetTimestamp]);

    const pause = useCallback(() => {
        if (isRunning()) {
            clearInterval();
            if (onPause) onPause();
        }
    }, [isRunning, onPause]);

    const stop = useCallback(() => {
        if (!isStopped()) {
            clearInterval();
            setTime(initialTime.current);
            if (onStop) onStop();
        }
    }, [isStopped, onStop]);

    const restart = useCallback(() => {
        setTime(initialTime.current);
        start();
    }, [start]);

    useEffect(() => {
        const newInitialTime = calculateInitialTime();
        initialTime.current = newInitialTime;

        if (isRunning()) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset time when target timestamp changes while running
            setTime(newInitialTime);
        } else if (autoStart && newInitialTime > 0) {
            start();
        }
    }, [targetTimestamp, autoStart, start, isRunning, calculateInitialTime]);

    useEffect(() => {
        return clearInterval;
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            start,
            pause,
            stop,
            restart,
            setTime,
            isRunning,
            isPaused,
            isStopped,
            isCompleted,
            time
        }),
        [isCompleted, isPaused, isRunning, isStopped, pause, restart, start, stop, time]
    );

    const minutes = Math.floor(time / 60);
    const seconds = time % 60;

    return (
        <div className={className}>
            <div className="flex flex-row">
                <TimeSlider value={zeroPad(minutes)} />:<TimeSlider value={zeroPad(seconds)} />
            </div>
        </div>
    );
}

function TimeSlider({ value }: { value: string }) {
    const nodeRef = useRef<HTMLDivElement>(null);

    return (
        <SwitchTransition mode="out-in">
            <CSSTransition
                key={value}
                nodeRef={nodeRef}
                timeout={{
                    appear: 150,
                    enter: 700,
                    exit: 150
                }}
                classNames={{
                    enter: timeSliderStyles.fadeEnter,
                    enterActive: timeSliderStyles.fadeEnterActive,
                    exit: timeSliderStyles.fadeExit,
                    exitActive: timeSliderStyles.fadeExitActive
                }}>
                <div ref={nodeRef}>{value}</div>
            </CSSTransition>
        </SwitchTransition>
    );
}

export default forwardRef(Countdown);
